import { Text } from "@chakra-ui/react";
import { Flex } from "@chakra-ui/layout";
import colors from "resources/theme/colors";
import moment from "moment";
import { useState, useEffect, useRef } from "react";
import { DateRangePicker as ReactDateRange } from "react-date-range";
import "react-date-range/dist/styles.css"; // main style file
import "react-date-range/dist/theme/default.css"; // theme css file
import { BiChevronDown } from "react-icons/bi";
import { Popover } from "antd";
import { ptBR } from "date-fns/locale";
import OutlineButton from "ui/components/Button/OutlineButton";
import { maskDateBr } from "infra/helpers/maskDateBr";
import { useMemo } from "react";
import { AiOutlineCloseCircle } from "react-icons/ai";
import DateRangePickerClearButton from "./components/ClearButton";
import { showError } from "infra/helpers/alerta";

interface DateRangePickerProps {
  value: {
    dataInicio?: string;
    dataFim?: string;
  };
  onChange: (dataInicio?: string, dataFim?: string) => any;
  isInvalid?: boolean;
  disabled?: boolean;
  minDate?: Date;
  maxDate?: Date;
  customStaticRanges?: StaticRange[];
  extraStaticRanges?: StaticRange[];
  allowEmptyDates?: {
    dataInicio?: boolean;
    dataFim?: boolean;
  };
  maxRangeDays?: number;
  placeholder?: string;
}

interface SelectionRange {
  startDate?: Date;
  endDate?: Date;
  key?: string;
}

function DateRangePicker({
  value,
  onChange,
  isInvalid,
  disabled,
  minDate,
  maxDate,
  customStaticRanges,
  extraStaticRanges = [],
  allowEmptyDates = {
    dataInicio: true,
    dataFim: true,
  },
  maxRangeDays,
  placeholder = "Selecione o período",
}: DateRangePickerProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [containerRefSet, setContainerRefSet] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [localRange, setLocalRange] = useState({
    startDate: value.dataInicio ? moment(value.dataInicio).toDate() : undefined,
    endDate: value.dataFim ? moment(value.dataFim).toDate() : undefined,
    key: "selection",
    color: colors.primary,
  });
  const [showInvalidInput, setShowInvalidInput] = useState(false);

  const setLocal = ({ selection }: { selection: SelectionRange }) => {
    if (
      maxRangeDays &&
      moment(selection.endDate).diff(selection.startDate, "days") > maxRangeDays
    ) {
      return showError(`O período máximo de busca é de ${maxRangeDays} dias.`);
    }

    setLocalRange((state) => ({
      ...state,
      startDate: selection.startDate
        ? moment(selection.startDate).toDate()
        : undefined,
      endDate: selection.endDate
        ? moment(selection.endDate).toDate()
        : undefined,
    }));
  };

  useEffect(() => {
    if (containerRef.current) {
      const inputSpanContainers =
        containerRef.current.getElementsByClassName("rdrDateInput");

      const startDateInput =
        inputSpanContainers[0]?.getElementsByTagName("input")?.[0];
      const endDateInput =
        inputSpanContainers[1]?.getElementsByTagName("input")?.[0];

      const originalHandlerStartDateInput = startDateInput.oninput;
      const originalHandlerEndDateInput = startDateInput.oninput;

      const onInput = (
        ev: Event,
        prop: "startDate" | "endDate",
        input: HTMLInputElement
      ) => {
        setShowInvalidInput(false);
        const value = (ev.target as any)?.value;

        const maskedValue = maskDateBr(value);
        input.value = maskedValue;

        const matchesFormat = maskedValue.match(/^\d{2}\/\d{2}\/\d{4}$/);
        const isValidDate =
          moment(maskedValue, "DD/MM/YYYY", true).isValid() ||
          (prop === "startDate" && allowEmptyDates.dataInicio && !value) ||
          (prop === "endDate" && allowEmptyDates.dataFim && !value);

        if (isValidDate) {
          if (
            maxRangeDays &&
            Math.abs(
              moment(maskedValue, "DD/MM/YYYY").diff(
                prop === "startDate"
                  ? localRange.endDate
                  : localRange.startDate,
                "days"
              )
            ) > maxRangeDays
          ) {
            return showError(
              `O período máximo de busca é de ${maxRangeDays} dias.`
            );
          }

          setLocalRange((state) => ({
            ...state,
            [prop]: value
              ? moment(maskedValue, "DD/MM/YYYY").toDate()
              : undefined,
          }));
        } else if (matchesFormat) {
          setShowInvalidInput(true);
        }
      };

      startDateInput.oninput = (ev) => {
        onInput(ev, "startDate", startDateInput);
      };

      endDateInput.oninput = (ev) => {
        onInput(ev, "endDate", endDateInput);
      };

      return () => {
        startDateInput.oninput = originalHandlerStartDateInput;
        endDateInput.oninput = originalHandlerEndDateInput;
      };
    }
  }, [
    allowEmptyDates,
    containerRef,
    containerRefSet,
    localRange.endDate,
    localRange.startDate,
    maxRangeDays,
  ]);

  useEffect(() => {
    setShowInvalidInput(false);
  }, [localRange]);

  const _staticRanges = useMemo(
    () =>
      customStaticRanges
        ? makeStaticRanges(customStaticRanges)
        : makeStaticRanges(staticRanges.concat(extraStaticRanges)),
    [customStaticRanges, extraStaticRanges]
  );

  return (
    <Popover
      overlayClassName="antd-popover-hide-arrow"
      arrowContent={<></>}
      overlayStyle={{
        borderRadius: 12,
        zIndex: 2,
      }}
      overlayInnerStyle={{
        borderRadius: 12,
        boxShadow: "0 0 6px 2px #87878725",
      }}
      placement="bottomRight"
      color="white"
      trigger="click"
      getPopupContainer={(triggerNode: any) => {
        return triggerNode.parentNode;
      }}
      content={
        <Flex
          flexDir="column"
          position="relative"
          ref={(ref) => {
            containerRef.current = ref;
            setContainerRefSet(true);
          }}
        >
          <ReactDateRange
            key={`${value}`}
            disabled={disabled}
            ranges={[localRange]}
            onChange={setLocal}
            locale={ptBR}
            retainEndDateOnFirstSelection
            editableDateInputs
            weekdayDisplayFormat="ccccc"
            dateDisplayFormat="dd/MM/yyyy"
            startDatePlaceholder="Início"
            endDatePlaceholder="Fim"
            ariaLabels={{
              monthPicker: "Mês",
              yearPicker: "Ano",
              prevButton: "Anterior",
              nextButton: "Próximo",
            }}
            inputRanges={[]}
            staticRanges={_staticRanges}
            minDate={minDate}
            maxDate={maxDate}
          />
          <Flex alignItems="center" justifyContent="flex-end">
            {showInvalidInput ? (
              <Text color="red">Data incorreta digitada</Text>
            ) : null}
            <OutlineButton
              ml={16}
              px={10}
              onClick={() => {
                if (
                  (moment(localRange.startDate).isValid() ||
                    (allowEmptyDates.dataInicio && !localRange.startDate)) &&
                  (moment(localRange.endDate).isValid() ||
                    (allowEmptyDates.dataFim && !localRange.endDate))
                ) {
                  onChange(
                    localRange.startDate
                      ? moment(localRange.startDate).format("YYYY-MM-DD")
                      : undefined,
                    localRange.endDate
                      ? moment(localRange.endDate).format("YYYY-MM-DD")
                      : undefined
                  );
                  setIsOpen(false);
                  setShowInvalidInput(false);
                } else {
                  setShowInvalidInput(true);
                }
              }}
            >
              Ok
            </OutlineButton>
          </Flex>
          {allowEmptyDates.dataInicio && localRange.startDate ? (
            <DateRangePickerClearButton
              right="170px"
              onClear={() => {
                setLocal({
                  selection: {
                    startDate: undefined,
                    endDate: localRange.endDate,
                  },
                });
              }}
            />
          ) : null}

          {allowEmptyDates.dataFim && localRange.endDate ? (
            <DateRangePickerClearButton
              right="11px"
              onClear={() => {
                setLocal({
                  selection: {
                    startDate: localRange.startDate,
                    endDate: undefined,
                  },
                });
              }}
            />
          ) : null}
        </Flex>
      }
      onVisibleChange={!disabled ? setIsOpen : undefined}
      visible={disabled ? false : isOpen}
    >
      <Flex
        position="relative"
        bg={disabled ? colors.borderGray : colors.white}
        borderRadius={6}
        borderWidth={1}
        borderColor={colors.borderGray}
        w="100%"
        minW={220}
        cursor="pointer"
        onClick={() => {
          if (!disabled) {
            setIsOpen(true);
          }
        }}
        pl={4}
        pr={2}
        py={2}
        transition="all 0.3s"
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
        _hover={
          !disabled
            ? {
                boxShadow: `0 0 0 2px ${
                  isInvalid ? colors.red : colors.primary
                }`,
                outlineColor: isInvalid ? colors.red : colors.primary,
              }
            : undefined
        }
        _active={
          !disabled
            ? {
                boxShadow: `0 0 0 2px ${
                  isInvalid ? colors.red : colors.primary
                }`,
                outlineColor: isInvalid ? colors.red : colors.primary,
              }
            : undefined
        }
      >
        <Text noOfLines={1} mr="auto">
          {value.dataInicio
            ? moment(value.dataInicio).format("DD/MM/YYYY")
            : value.dataFim
            ? "Desde o início até "
            : ""}
          {value.dataInicio && value.dataFim ? " - " : ""}
          {value.dataFim
            ? moment(value.dataFim).format("DD/MM/YYYY")
            : value.dataInicio
            ? " até hoje"
            : ""}
          {!value.dataInicio && !value.dataFim ? placeholder : ""}
        </Text>
        <Flex position="relative">
          <BiChevronDown
            size={20}
            color={colors.black}
            style={{
              transition: "all 0.25s linear",
              transform: isOpen ? "scale(1, -1)" : "scale(1, 1)",
            }}
          />

          {(value.dataInicio || value.dataFim) &&
          allowEmptyDates.dataInicio &&
          allowEmptyDates.dataFim ? (
            <Flex
              opacity={isHovering ? 1 : 0}
              position="absolute"
              top="0"
              left="0"
              right="0"
              bottom="0"
              margin="auto"
              bg="white"
              transition="opacity 0.23s ease"
              onClick={(ev) => {
                ev.stopPropagation();
                setLocal({
                  selection: {
                    startDate: undefined,
                    endDate: undefined,
                  },
                });

                onChange(undefined, undefined);
              }}
            >
              <AiOutlineCloseCircle size={20} color={colors.gray} />
            </Flex>
          ) : null}
        </Flex>
      </Flex>
    </Popover>
  );
}

interface StaticRange {
  label: string;
  range: () => SelectionRange;
}

const makeStaticRanges = (ranges: StaticRange[]) =>
  ranges.map((r) => ({ ...staticRangeHandler, ...r }));

const staticRangeHandler = {
  range: {} as any,
  isSelected(range: SelectionRange) {
    const definedRange = this.range();
    return (
      (definedRange.startDate
        ? moment(range.startDate).isSame(definedRange.startDate, "day")
        : true) &&
      (definedRange.endDate
        ? moment(range.endDate).isSame(definedRange.endDate, "day")
        : true)
    );
  },
};

const staticRanges: StaticRange[] = [
  {
    label: "Hoje",
    range: () => ({
      startDate: moment().startOf("day").toDate(),
      endDate: moment().endOf("day").toDate(),
    }),
  },
  {
    label: "Ontem e hoje",
    range: () => ({
      startDate: moment().subtract(1, "days").startOf("day").toDate(),
      endDate: moment().endOf("day").toDate(),
    }),
  },
  {
    label: "Essa semana",
    range: () => ({
      startDate: moment().startOf("week").toDate(),
      endDate: moment().endOf("week").toDate(),
    }),
  },
  {
    label: "Semana passada",
    range: () => ({
      startDate: moment().subtract(7, "days").startOf("week").toDate(),
      endDate: moment().subtract(7, "days").endOf("week").toDate(),
    }),
  },
  {
    label: "Esse mês",
    range: () => ({
      startDate: moment().startOf("month").toDate(),
      endDate: moment().endOf("month").toDate(),
    }),
  },
  {
    label: "Mês passado",
    range: () => ({
      startDate: moment().subtract(1, "month").startOf("month").toDate(),
      endDate: moment().subtract(1, "month").endOf("month").toDate(),
    }),
  },
];

export default DateRangePicker;
